import type { HexColor } from 'terra-draw'

// Delivery boundary color (green, used for polygon_type: 'base')
export const BASE_DELIVERY_AREA_COLOR = '#16A34A'
export const BASE_DELIVERY_AREA_FILL_OPACITY = 0.08
export const DEFAULT_DELIVERY_AREA_FILL_OPACITY = 0.35
export const NON_DELIVERY_AREA_FILL_OPACITY = 0.35

// Specialty price place color (Amber - used for polygon_type: 'location')
export const SPECIALTY_LOCATION_COLOR = '#F59E0B'

// Alternate colors distinguish read-only boundaries from other locations.
export const DELIVERY_AREA_COLORS = [
  BASE_DELIVERY_AREA_COLOR,
  '#34A853', // Green
  '#FBBC04', // Yellow/Gold
  '#FF9800', // Orange
  '#00BCD4', // Cyan
  '#009688', // Teal
  '#4169E1', // Royal Blue
  '#795548', // Brown
  '#607D8B', // Blue Grey
  '#2196F3', // Blue
  '#8BC34A', // Light Green
] as const satisfies readonly HexColor[]

// Maximum number of drawn map rules allowed per type
export const MAX_EXCLUDED_AREAS = 10
export const MAX_SPECIALTY_LOCATIONS = 10

// No-delivery area color (Red - used for polygon_type: 'excluded')
export const NON_DELIVERY_AREA_COLOR = '#DC2626'

export const getSemanticPolygonColor = (
  polygonType: string | null | undefined,
  fallbackColor?: string | null
): string => {
  if (polygonType === 'location') return SPECIALTY_LOCATION_COLOR
  if (polygonType === 'excluded') return NON_DELIVERY_AREA_COLOR
  if (polygonType === 'base') return BASE_DELIVERY_AREA_COLOR

  return fallbackColor || BASE_DELIVERY_AREA_COLOR
}

export const getSemanticPolygonFillOpacity = (
  polygonType: string | null | undefined,
  fallbackOpacity?: number | null
): number => {
  if (polygonType === 'base') return BASE_DELIVERY_AREA_FILL_OPACITY
  if (polygonType === 'excluded') return NON_DELIVERY_AREA_FILL_OPACITY

  return fallbackOpacity ?? DEFAULT_DELIVERY_AREA_FILL_OPACITY
}

// Default fee for a new special price place
export const DEFAULT_SPECIALTY_LOCATION_FEE = 9.99

export const POLYGON_Z_INDEX = {
  base: 1,
  location: 25,
  excluded: 15,

  otherLocationBase: 0,
  otherLocationLocation: 7,
}

/**
 * Get z-index for a polygon based on its polygon_type
 * @param polygonType - The polygon_type value ('base', 'location', 'excluded')
 * @param isOtherLocation - Whether this is a read-only polygon from another location
 * @returns The z-index value
 */
export const getZIndexForPolygonType = (
  polygonType: string | null | undefined,
  isOtherLocation = false
): number => {
  if (isOtherLocation) {
    switch (polygonType) {
      case 'base':
        return POLYGON_Z_INDEX.otherLocationBase
      case 'location':
        return POLYGON_Z_INDEX.otherLocationLocation
      case null:
      case undefined:
      default:
        return POLYGON_Z_INDEX.otherLocationBase
    }
  }

  switch (polygonType) {
    case 'base':
      return POLYGON_Z_INDEX.base
    case 'location':
      return POLYGON_Z_INDEX.location
    case 'excluded':
      return POLYGON_Z_INDEX.excluded
    case null:
    case undefined:
    default:
      return POLYGON_Z_INDEX.base // Default to base z-index
  }
}
